import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { PasswordInput } from "@/components/PasswordInput";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "CENTRAL") return <Navigate to="/centrale" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      navigate(res.redirect_to || "/centrale");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center bg-navy px-5">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl sm:p-10"
      >
        <div className="flex items-center gap-3">
          <Logo boxed className="h-8 w-auto" boxClassName="rounded-xl px-3 py-2" />
          <div>
            <p className="font-display text-2xl font-semibold text-white">cozyhome</p>
            <p className="text-xs uppercase tracking-widest text-white/50">Espace pro</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-9 space-y-5" data-testid="admin-login-form">
          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-white/60">Email</span>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3.5 text-sm text-white outline-none transition-colors duration-300 focus:border-cognac placeholder:text-white/30"
              placeholder="votre@email.fr"
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-white/60">Mot de passe</span>
            <PasswordInput
              testId="admin-login-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3.5 text-sm text-white outline-none transition-colors duration-300 focus:border-cognac placeholder:text-white/30"
              iconClassName="text-white/50 hover:text-white"
              placeholder="••••••••"
            />
          </label>

          {error && (
            <p data-testid="admin-login-error" className="rounded-xl bg-red-500/15 px-4 py-3 text-sm font-semibold text-red-300">
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-cognac py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-cream hover:text-navy disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Se connecter
          </button>
        </form>

        <Link
          data-testid="admin-login-back-link"
          to="/"
          className="mt-7 flex items-center justify-center gap-2 text-xs font-semibold text-white/40 transition-colors duration-300 hover:text-cognac-light"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Retour au site
        </Link>
      </motion.div>
    </div>
  );
}
